import React, { useState, useEffect } from 'react';
import { crearBajaEstudiante, getBajasEstudiante, reactivarBajaEstudiante } from '../../api/students';
import { getCycles } from '../../api/ciclosEscolares';
import styles from '../../styles/BajaReactivacionStyles/BajaReactivacion.module.css';

function StudentDetail({ student, onBack, onSaved }) {
  const [activeTab, setActiveTab] = useState('baja');
  const [ciclos, setCiclos] = useState([]);
  const [bajaExistente, setBajaExistente] = useState(null);
  const [saving, setSaving] = useState(false);
  const [mensaje, setMensaje] = useState(null);

  const [formData, setFormData] = useState({
    fechaBaja: new Date().toISOString().slice(0, 10),
    tipoBaja: 'TEMPORAL',
    motivo: 'VOLUNTARIA',
    descripcion: '',
    autorizacion: '',
    idCiclo: '',
  });

  const [reactivacionData, setReactivacionData] = useState({
    fechaReactivacion: new Date().toISOString().slice(0, 10),
    cicloEscolar: '',
    descripcion: '',
  });

  // Helpers para resolver datos del objeto student
  const resolveNombre = () => student.nombre_completo || student.nombre || '-';
  const resolveCampus = () => {
    if (student.id_campus && typeof student.id_campus === 'object') return student.id_campus.nombre_campus || '-';
    return student.campus_nombre || student.campus || '-';
  };
  const resolveSeccion = () => {
    if (student.id_seccion && typeof student.id_seccion === 'object') return student.id_seccion.nombre_seccion || '-';
    return student.seccion_nombre || student.seccion || '-';
  };
  const resolvePrograma = () => {
    if (student.id_programa_actual && typeof student.id_programa_actual === 'object') return student.id_programa_actual.nombre_programa || '-';
    return student.programa_nombre || student.programa || '-';
  };
  const resolveEstatus = () => student.estatus_estudiante || student.estadoActual || '-';
  const resolveCicloActual = () => {
    if (student.ciclo_actual && student.ciclo_actual.nombre_ciclo) return student.ciclo_actual.nombre_ciclo;
    if (student.ciclo_ingreso && typeof student.ciclo_ingreso === 'object') return student.ciclo_ingreso.nombre_ciclo || '-';
    return student.ciclo_escolar || '-';
  };
  const resolveAdeudos = () => {
    if (student.estado_financiero?.saldo_vencido > 0) return `$${student.estado_financiero.saldo_vencido.toFixed(2)}`;
    return 'Sin adeudos';
  };
  const tieneAdeudos = student.estado_financiero?.saldo_vencido > 0;

  const resolveInstitucion = () => {
    if (student.id_institucion && typeof student.id_institucion === 'object') return student.id_institucion.nombre_institucion || '-';
    return student.institucion_nombre || student.institucion || '-';
  };

  useEffect(() => {
    const id = student.id_estudiante || student.id;
    if (!id) return;
    // Cargar ciclos escolares
    getCycles().then(setCiclos).catch(() => setCiclos([]));
    // Cargar baja existente (para tab reactivación)
    getBajasEstudiante(id).then((bajas) => {
      if (bajas.length > 0) {
        const ultima = bajas[bajas.length - 1];
        setBajaExistente(ultima);
        setFormData(prev => ({ ...prev, idCiclo: String(ultima.id_ciclo_escolar || '') }));
      }
    }).catch(() => {});
  }, [student]);

  const handleInputChange = (field, value) => setFormData(prev => ({ ...prev, [field]: value }));
  const handleReactivacionInputChange = (field, value) => setReactivacionData(prev => ({ ...prev, [field]: value }));

  const handleSubmitBaja = async (e) => {
    e.preventDefault();
    if (!formData.fechaBaja) { alert('La fecha de baja es obligatoria.'); return; }
    if (!formData.tipoBaja) { alert('Selecciona el tipo de baja.'); return; }
    if (!formData.motivo) { alert('El motivo es obligatorio.'); return; }
    if (!formData.idCiclo) { alert('El ciclo escolar es obligatorio.'); return; }

    setSaving(true);
    setMensaje(null);
    try {
      const idEstudiante = student.id_estudiante || student.id;
      await crearBajaEstudiante({
        id_estudiante: idEstudiante,
        id_ciclo_escolar: Number(formData.idCiclo),
        tipo_baja: formData.tipoBaja,
        motivo_baja: formData.motivo,
        descripcion_detallada: formData.descripcion || '-',
        fecha_baja: formData.fechaBaja,
        autorizado_por: formData.autorizacion || 1,
        tiene_adeudos: tieneAdeudos,
      });
      setMensaje({ tipo: 'exito', texto: `Baja ${formData.tipoBaja === 'TEMPORAL' ? 'temporal' : 'definitiva'} registrada correctamente.` });
      if (onSaved) onSaved();
    } catch (err) {
      const detail = err?.response?.data;
      const msg = typeof detail === 'object' ? JSON.stringify(detail) : (detail || err.message);
      setMensaje({ tipo: 'error', texto: `Error al registrar baja: ${msg}` });
    } finally {
      setSaving(false);
    }
  };

  const handleSubmitReactivacion = async (e) => {
    e.preventDefault();
    if (!reactivacionData.fechaReactivacion) { alert('La fecha de reactivación es obligatoria.'); return; }
    if (!reactivacionData.cicloEscolar) { alert('El ciclo escolar de reingreso es obligatorio.'); return; }

    setSaving(true);
    setMensaje(null);
    try {
      if (bajaExistente) {
        await reactivarBajaEstudiante(bajaExistente.id_baja, {
          fecha_reactivacion: reactivacionData.fechaReactivacion,
          id_ciclo_escolar: Number(reactivacionData.cicloEscolar),
        });
      } else {
        // Si no hay baja previa, crear una baja+reactivación directa
        const idEstudiante = student.id_estudiante || student.id;
        const baja = await crearBajaEstudiante({
          id_estudiante: idEstudiante,
          id_ciclo_escolar: Number(reactivacionData.cicloEscolar),
          tipo_baja: 'TEMPORAL',
          motivo_baja: 'VOLUNTARIA',
          descripcion_detallada: reactivacionData.descripcion || '-',
          fecha_baja: reactivacionData.fechaReactivacion,
          autorizado_por: 1,
          tiene_adeudos: false,
        });
        await reactivarBajaEstudiante(baja.id_baja, {
          fecha_reactivacion: reactivacionData.fechaReactivacion,
          id_ciclo_escolar: Number(reactivacionData.cicloEscolar),
        });
      }
      setMensaje({ tipo: 'exito', texto: 'Reactivación registrada. Estatus actualizado a ACTIVO.' });
      if (onSaved) onSaved();
    } catch (err) {
      const detail = err?.response?.data;
      const msg = typeof detail === 'object' ? JSON.stringify(detail) : (detail || err.message);
      setMensaje({ tipo: 'error', texto: `Error al reactivar: ${msg}` });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className={styles.detailContainer}>
      {/* Back Button */}
      <button className={styles.backButton} onClick={onBack}>
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
          <path d="M19 12H5"/>
          <path d="M12 19l-7-7 7-7"/>
        </svg>
        Volver a búsqueda
      </button>

      {/* Student Info Card */}
      <div className={styles.studentInfoSection}>
        <div className={styles.studentHeader}>
          <h3>Estudiante: {resolveNombre()}</h3>
        </div>
        
        <div className={styles.studentCard}>
          <div className={styles.studentAvatar}>
            <svg viewBox="0 0 24 24" fill="currentColor">
              <path d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z"/>
            </svg>
          </div>
          
          <div className={styles.studentData}>
            <div className={styles.studentDataRow}>
              <div className={styles.dataColumn}>
                <div className={styles.dataItem}>
                  <span className={styles.dataLabel}>Matrícula</span>
                  <span className={styles.dataValue}>{student.matricula || '-'}</span>
                </div>
                <div className={styles.dataItem}>
                  <span className={styles.dataLabel}>Sección</span>
                  <span className={styles.dataValue}>{resolveSeccion()}</span>
                </div>
                <div className={styles.dataItem}>
                  <span className={styles.dataLabel}>Programa</span>
                  <span className={styles.dataValue}>{resolvePrograma()}</span>
                </div>
              </div>
              
              <div className={styles.dataColumn}>
                <div className={styles.dataItem}>
                  <span className={styles.dataLabel}>Ciclo Actual</span>
                  <span className={styles.dataValue}>{resolveCicloActual()}</span>
                </div>
                <div className={styles.dataItem}>
                  <span className={styles.dataLabel}>Institución</span>
                  <span className={styles.dataValue}>{resolveInstitucion()}</span>
                </div>
                <div className={styles.dataItem}>
                  <span className={styles.dataLabel}>Campus</span>
                  <span className={styles.dataValue}>{resolveCampus()}</span>
                </div>
              </div>
              
              <div className={styles.dataColumn}>
                <div className={styles.dataItem}>
                  <span className={styles.dataLabel}>Estatus</span>
                  <span className={styles.dataValue}>{resolveEstatus()}</span>
                </div>
                <div className={styles.dataItem}>
                  <span className={styles.dataLabel}>Adeudos financieros</span>
                  <span className={`${styles.dataValue} ${tieneAdeudos ? '' : styles.noDebt}`}>
                    {tieneAdeudos ? '' : <span className={styles.statusDot}>✓</span>}
                    {resolveAdeudos()}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Mensaje de resultado */}
      {mensaje && (
        <div style={{
          margin: '12px 0',
          padding: '10px 16px',
          borderRadius: 6,
          background: mensaje.tipo === 'exito' ? '#d1fae5' : '#fee2e2',
          color: mensaje.tipo === 'exito' ? '#065f46' : '#991b1b',
          fontSize: 13,
          border: `1px solid ${mensaje.tipo === 'exito' ? '#6ee7b7' : '#fca5a5'}`,
        }}>
          {mensaje.texto}
        </div>
      )}

      {/* Tabs Section */}
      <div className={styles.tabsSection}>
        <div className={styles.tabsHeader}>
          <button
            className={`${styles.tab} ${activeTab === 'baja' ? styles.tabActive : ''}`}
            onClick={() => setActiveTab('baja')}
          >
            Baja del ciclo escolar
          </button>
          <button
            className={`${styles.tab} ${activeTab === 'reactivacion' ? styles.tabActive : ''}`}
            onClick={() => setActiveTab('reactivacion')}
          >
            Reactivación
          </button>
        </div>

        <div className={styles.tabContent}>
          {activeTab === 'baja' ? (
            <form onSubmit={handleSubmitBaja} className={styles.form}>
              <div className={styles.formRow}>
                <div className={styles.formGroup}>
                  <label className={styles.formLabel}>Fecha de baja</label>
                  <div className={styles.dateInputContainer}>
                    <input
                      type="date"
                      className={styles.dateInput}
                      value={formData.fechaBaja}
                      onChange={(e) => handleInputChange('fechaBaja', e.target.value)}
                      required
                    />
                  </div>
                </div>

                <div className={styles.formGroup}>
                  <label className={styles.formLabel}>Tipo de Baja</label>
                  <select
                    className={styles.formSelect}
                    value={formData.tipoBaja}
                    onChange={(e) => handleInputChange('tipoBaja', e.target.value)}
                    required
                  >
                    <option value="TEMPORAL">Baja Temporal</option>
                    <option value="DEFINITIVA">Baja Definitiva</option>
                  </select>
                </div>
                
                <div className={styles.formGroup}>
                  <label className={styles.formLabel}>Motivo</label>
                  <select
                    className={styles.formSelect}
                    value={formData.motivo}
                    onChange={(e) => handleInputChange('motivo', e.target.value)}
                    required
                  >
                    <option value="VOLUNTARIA">Voluntaria</option>
                    <option value="ACADEMICA">Académica</option>
                    <option value="DISCIPLINARIA">Disciplinaria</option>
                    <option value="FINANCIERA">Financiera</option>
                    <option value="PERSONAL">Personal</option>
                  </select>
                </div>
              </div>

              <div className={styles.formRow}>
                <div className={styles.formGroup}>
                  <label className={styles.formLabel}>Ciclo Escolar</label>
                  <select
                    className={styles.formSelect}
                    value={formData.idCiclo}
                    onChange={(e) => handleInputChange('idCiclo', e.target.value)}
                    required
                  >
                    <option value="">- Seleccionar ciclo -</option>
                    {ciclos.map((c) => (
                      <option key={c.id_ciclo_escolar} value={c.id_ciclo_escolar}>
                        {c.nombre_ciclo || c.name}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
              
              <div className={styles.formRow}>
                <div className={styles.formGroup}>
                  <label className={styles.formLabel}>Descripción Detallada</label>
                  <textarea
                    className={styles.formTextarea}
                    value={formData.descripcion}
                    onChange={(e) => handleInputChange('descripcion', e.target.value)}
                    placeholder="Escribir descripción..."
                  />
                </div>
              </div>

              {/* Adeudos financieros */}
              <div className={styles.adeudosSection}>
                <div className={styles.adeudosHeader}>
                  <span className={styles.adeudosIcon}>{tieneAdeudos ? '⚠' : '✓'}</span>
                  <span className={styles.adeudosText}>{tieneAdeudos ? 'Con adeudos' : 'Sin adeudos'}</span>
                  <span className={styles.adeudosSubtext}>
                    {tieneAdeudos ? `Saldo vencido: ${resolveAdeudos()}` : 'El alumno está al corriente'}
                  </span>
                </div>
              </div>
              
              <div className={styles.formActions}>
                <button type="submit" className={styles.applyButton} disabled={saving}>
                  {saving ? 'Guardando...' : 'APLICAR'}
                </button>
              </div>
            </form>
          ) : (
            <form onSubmit={handleSubmitReactivacion} className={styles.form}>
              <div className={styles.formRow}>
                <div className={styles.formGroup}>
                  <label className={styles.formLabel}>Fecha de reactivación</label>
                  <div className={styles.dateInputContainer}>
                    <input
                      type="date"
                      className={styles.dateInput}
                      value={reactivacionData.fechaReactivacion}
                      onChange={(e) => handleReactivacionInputChange('fechaReactivacion', e.target.value)}
                      required
                    />
                  </div>
                </div>
                
                <div className={styles.formGroup}>
                  <label className={styles.formLabel}>Ciclo escolar de reingreso</label>
                  <select
                    className={styles.formSelect}
                    value={reactivacionData.cicloEscolar}
                    onChange={(e) => handleReactivacionInputChange('cicloEscolar', e.target.value)}
                    required
                  >
                    <option value="">- Seleccionar ciclo escolar -</option>
                    {ciclos.map((c) => (
                      <option key={c.id_ciclo_escolar} value={c.id_ciclo_escolar}>
                        {c.nombre_ciclo || c.name}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
              
              <div className={styles.formRow}>
                <div className={styles.formGroup}>
                  <label className={styles.formLabel}>Descripción Detallada</label>
                  <textarea
                    className={styles.formTextarea}
                    value={reactivacionData.descripcion}
                    onChange={(e) => handleReactivacionInputChange('descripcion', e.target.value)}
                    placeholder="Escribir descripción..."
                  />
                </div>
              </div>

              {bajaExistente && (
                <div style={{ background: '#f0f4ff', borderRadius: 6, padding: '10px 14px', marginBottom: 12, fontSize: 13, color: '#374151' }}>
                  Baja registrada el <strong>{bajaExistente.fecha_baja}</strong> - Tipo: <strong>{bajaExistente.tipo_baja}</strong>
                </div>
              )}
              
              <div className={styles.formActions}>
                <button type="submit" className={styles.applyButton} disabled={saving}>
                  {saving ? 'Guardando...' : 'APLICAR'}
                </button>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}

export default StudentDetail;